import { useMemo, useState } from 'react';
import type { DataISO, Dipendente, Evento } from '../api/tipi.ts';
import { indicizzaEventi } from '../calendario/griglia.ts';
import stili from './ChiEAssente.module.css';

/**
 * Chi e' assente, in sola lettura.
 *
 * Sostituisce il calendario sul telefono: la stessa domanda — chi manca e quando
 * — risposta con un elenco invece che con una griglia da 365 colonne.
 *
 * Mostra tutti i reparti, come il file condiviso di oggi: chiunque puo' gia'
 * vedere le assenze di chiunque, e restringerlo sarebbe un cambiamento di
 * comportamento, non una traduzione.
 */

export interface ProprietaChiEAssente {
  dipendenti: readonly Dipendente[];
  eventi: readonly Evento[];
}

const GIORNI_VISIBILI = 14;

export function ChiEAssente({ dipendenti, eventi }: ProprietaChiEAssente) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const [reparto, setReparto] = useState('tutti');
  const [giorno, setGiorno] = useState<DataISO>(oggi);

  const reparti = useMemo(() => {
    const insieme = new Map<string, string>();
    for (const d of dipendenti) {
      insieme.set(`${d.sedeCodice}/${d.repartoCodice}`, `${d.sedeNome} · ${d.repartoNome}`);
    }
    return [...insieme].sort((a, b) => a[1].localeCompare(b[1]));
  }, [dipendenti]);

  const indice = useMemo(() => indicizzaEventi(eventi), [eventi]);

  const giorni = useMemo(() => {
    const elenco: DataISO[] = [];
    let t = Date.parse(`${oggi}T00:00:00Z`);
    for (let i = 0; i < GIORNI_VISIBILI; i++) {
      elenco.push(new Date(t).toISOString().slice(0, 10));
      t += 86_400_000;
    }
    return elenco;
  }, [oggi]);

  const assenti = useMemo(() => {
    const filtrati =
      reparto === 'tutti'
        ? dipendenti
        : dipendenti.filter((d) => `${d.sedeCodice}/${d.repartoCodice}` === reparto);

    return filtrati
      .map((d) => ({ dipendente: d, eventi: indice.get(`${d.id}|${giorno}`) ?? [] }))
      .filter((r): r is { dipendente: Dipendente; eventi: Evento[] } => r.eventi.length > 0)
      .sort(
        (a, b) =>
          a.dipendente.repartoNome.localeCompare(b.dipendente.repartoNome) ||
          a.dipendente.nomeCompleto.localeCompare(b.dipendente.nomeCompleto),
      );
  }, [dipendenti, indice, giorno, reparto]);

  return (
    <div className={stili.pagina}>
      <h1 className={stili.titolo}>Chi è assente</h1>

      {/* I giorni scorrono in orizzontale: su un telefono e' il modo piu'
          naturale di spostarsi avanti di qualche giorno senza aprire un
          selettore di date. */}
      <div className={stili.giorni}>
        {giorni.map((g) => (
          <button
            key={g}
            type="button"
            className={`${stili.giorno} ${g === giorno ? stili.giornoAttivo : ''}`}
            onClick={() => setGiorno(g)}
          >
            <span className={stili.giornoSettimana}>{nomeGiorno(g)}</span>
            <span className={stili.giornoNumero}>{g.slice(8, 10)}</span>
            <span className={stili.giornoMese}>{mesePer(g)}</span>
          </button>
        ))}
      </div>

      <label className={stili.filtro}>
        <span className={stili.etichetta}>Reparto</span>
        <select
          className={stili.select}
          value={reparto}
          onChange={(e) => setReparto(e.target.value)}
        >
          <option value="tutti">Tutti i reparti</option>
          {reparti.map(([codice, nome]) => (
            <option key={codice} value={codice}>
              {nome}
            </option>
          ))}
        </select>
      </label>

      <p className={stili.conteggio}>
        {assenti.length === 0
          ? 'Nessun assente in questo giorno.'
          : assenti.length === 1
            ? '1 persona assente'
            : `${assenti.length} persone assenti`}
      </p>

      <ul className={stili.elenco}>
        {assenti.map(({ dipendente, eventi: eventiDelGiorno }) => (
          <li key={dipendente.id} className={stili.voce}>
            <div className={stili.voceTesto}>
              <span className={stili.voceNome}>{dipendente.nomeCompleto}</span>
              <span className={stili.voceReparto}>
                {dipendente.sedeNome} · {dipendente.repartoNome}
              </span>
            </div>
            <span className={stili.voceEvento}>
              {eventiDelGiorno.map((e) => e.etichetta).join(' + ')}
              {(() => {
                // Le ore le porta l'assenza, non la trasferta che le sta
                // accanto: si cerca il primo evento che ne ha.
                const conOre = eventiDelGiorno.find((e) => e.ore !== null);
                return conOre?.ore != null
                  ? ` · ${String(conOre.ore).replace('.', ',')} h`
                  : null;
              })()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function nomeGiorno(data: DataISO): string {
  return new Date(`${data}T00:00:00Z`)
    .toLocaleDateString('it-IT', { weekday: 'short', timeZone: 'UTC' })
    .slice(0, 3);
}

function mesePer(data: DataISO): string {
  return new Date(`${data}T00:00:00Z`).toLocaleDateString('it-IT', {
    month: 'short',
    timeZone: 'UTC',
  });
}
